// StudyDost — Terms & Conditions
// frontend/src/pages/TermsPage.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StaticPage, Section, SubSection, Meta, FooterLinks, staticStyles as s } from './PrivacyPage';

const LAST_UPDATED = 'May 3, 2026';

export default function TermsPage() {
  const navigate = useNavigate();
  return (
    <StaticPage title="Terms & Conditions" navigate={navigate}>
      <Meta>Last updated: {LAST_UPDATED} · Governing law: India</Meta>

      <Section title="1. Acceptance of Terms">
        <p>By accessing or using StudyDost (studydost.ai) or our WhatsApp service, you agree to be bound by these Terms and Conditions. These terms constitute a legally binding agreement between you and Let's Redefine Private Limited ("Company", "we", "us").</p>
        <p>If you are a minor (under 18 years of age), your parent or guardian must review and agree to these terms on your behalf. By using the service, you confirm that either you are 18 or older, or that your parent/guardian has consented.</p>
      </Section>

      <Section title="2. The Service">
        <p>StudyDost is an AI-powered educational assistance platform that helps students (Class 1–12) get answers to academic questions via WhatsApp and a web application. The service is intended solely for educational purposes.</p>
        <SubSection title="2.1 What we provide">
          <ul>
            <li>AI-generated answers to academic questions based on Indian school curricula</li>
            <li>Photo-based question solving using OCR technology</li>
            <li>Voice question support via Whisper AI transcription</li>
            <li>Study tracking, streaks, XP, and progress reports</li>
            <li>Teacher referral and parent dashboard features</li>
          </ul>
        </SubSection>
        <SubSection title="2.2 What we do not guarantee">
          <ul>
            <li>100% accuracy of AI-generated answers — always verify important answers with your teacher or textbook</li>
            <li>Uninterrupted availability — the service may be unavailable during maintenance or due to technical issues</li>
            <li>Results in examinations — StudyDost is a study aid, not a substitute for proper study and preparation</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="3. User Accounts">
        <p>You must provide a valid Indian phone number to create an account. You are responsible for maintaining the confidentiality of your account and for all activities that occur under your account. You must notify us immediately of any unauthorized use.</p>
        <p>We reserve the right to suspend or terminate accounts that violate these terms, are used fraudulently, or are inactive for more than 12 months.</p>
      </Section>

      <Section title="4. Acceptable Use">
        <p>You agree not to use StudyDost to:</p>
        <ul>
          <li>Submit content that is harmful, abusive, defamatory, or illegal</li>
          <li>Attempt to cheat in examinations or academic assessments (beyond using it as a study tool)</li>
          <li>Reverse-engineer, scrape, or abuse the API or WhatsApp integration</li>
          <li>Share your account with others or resell access to the service</li>
          <li>Submit personally identifiable information of third parties without consent</li>
          <li>Use the service for any commercial purpose other than personal study</li>
        </ul>
      </Section>

      <Section title="5. Payments and Subscriptions">
        <SubSection title="5.1 Plans">
          <p>StudyDost offers a free tier (5 questions/day) and paid plans (₹5/question, ₹199/month, ₹499/quarter, ₹999/year). Prices are in Indian Rupees (INR) and include applicable taxes.</p>
        </SubSection>
        <SubSection title="5.2 Billing">
          <p>Payments are processed by Razorpay. By subscribing, you authorise us to charge your payment method for the selected plan. Monthly and annual plans auto-renew unless cancelled before the renewal date.</p>
        </SubSection>
        <SubSection title="5.3 Refunds">
          <p>Please refer to our Refund Policy at studydost.ai/refund for full details on cancellations and refunds.</p>
        </SubSection>
      </Section>

      <Section title="6. Intellectual Property">
        <p>All content, branding, software, and technology used in StudyDost is owned by Let's Redefine Private Limited or its licensors. You may not copy, modify, distribute, or create derivative works without our express written permission.</p>
        <p>NCERT and board curriculum content referenced in answers is used for educational purposes under fair use principles. Diagrams sourced from Wikimedia Commons are used under their respective Creative Commons licences.</p>
      </Section>

      <Section title="7. AI-Generated Content Disclaimer">
        <p>Answers provided by StudyDost are generated by artificial intelligence and are intended as educational guidance only. While we strive for accuracy, AI can make mistakes. StudyDost answers should not be treated as definitive or used as the sole source of truth in any examination, medical, legal, or safety context.</p>
        <p>We are not liable for any consequences arising from reliance on AI-generated answers.</p>
      </Section>

      <Section title="8. Limitation of Liability">
        <p>To the maximum extent permitted by applicable Indian law, Let's Redefine Private Limited shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service, including but not limited to loss of data, loss of profits, or academic outcomes.</p>
        <p>Our total liability to you for any claim arising from these terms shall not exceed the amount you paid us in the 3 months preceding the claim.</p>
      </Section>

      <Section title="9. Governing Law and Disputes">
        <p>These terms are governed by the laws of India. Any disputes arising from these terms or your use of StudyDost shall be subject to the exclusive jurisdiction of the courts in Patna, Bihar, India.</p>
        <p>We encourage you to first contact us at hello@studydost.ai to resolve any dispute informally before initiating legal proceedings.</p>
      </Section>

      <Section title="10. Changes to Terms">
        <p>We may update these Terms from time to time. Material changes will be notified via WhatsApp or email. Continued use of the service after notification constitutes acceptance of the updated terms.</p>
      </Section>

      <Section title="11. Contact">
        <p><strong>Let's Redefine Private Limited</strong><br />
        Email: legal@studydost.ai<br />
        Website: studydost.ai<br />
        Patna, Bihar, India</p>
      </Section>
    </StaticPage>
  );
}
